import { useContext } from "react"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import { toast } from "@/hooks/useToast"
import { TimeWindowFormSchema } from "@/modules/patients/agenda/time-window/time-window-schema"
import { ProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { useSwissTimeWindowMutation } from "@/modules/patients/services/ch/useChCreateTimeWindow"
import { useChUpdateProgressNoteFromTimeWindow } from "@/modules/patients/services/ch/useChUpdateProgressNoteFromTimeWindow"
import useCallContextStore from "@/stores/useCallContextStore"

interface UseHandleSubmitParams {
  patient: PatientFragment$data
  onSuccess: () => void
}

export const useHandleSubmit = ({ patient, onSuccess }: UseHandleSubmitParams) => {
  const { t, i18n } = useTranslation()
  const patientId = usePatientParam()
  const { mutateTimeWindow } = useSwissTimeWindowMutation(patientId, i18n.language)
  const [syncProgressNote] = useChUpdateProgressNoteFromTimeWindow()
  const progressNoteFormStore = useContext(ProgressNoteFormContext)
  if (!progressNoteFormStore) throw new Error("Missing ProgressNoteFormProvider in the tree")

  return (data: TimeWindowFormSchema) => {
    const isCorrection = data.correction === "timeWindowErrorCorrection"
    const referenceDate = isCorrection && data.referenceDate ? format(data.referenceDate, "yyyy-MM-dd") : null
    const referralToCareProviderId = data.referralToCareProviderId ? data.referralToCareProviderId : null
    // The external care provider only belongs to the "Care provider" referred by; never send it otherwise.
    const referredByExternalCareProviderId =
      data.referredBy === "CARE_PROVIDER" && data.referredByExternalCareProviderId
        ? data.referredByExternalCareProviderId
        : null

    if (data.id) {
      mutateTimeWindow({
        kind: "update",
        input: {
          id: data.id,
          involvesPreventionProgram: data.involvesPreventionProgram,
          referralToCareProviderId,
          referredByExternalCareProviderId,
          referralToDoctorName: data.nameOfDoctor,
          dateFrom: format(data.dateFrom, "yyyy-MM-dd"),
          dateTo: format(data.dateTo, "yyyy-MM-dd"),
          referralReason: data.referralReason,
          referredBy: data.referredBy,
          internalComment: data.internalComment,
          errorCorrection: isCorrection,
          referenceDate,
          alternativeTriage: data.alternativeTriage,
        },
        onUpdateSuccess: ({ ch_updateTimeWindow }) => {
          if (!ch_updateTimeWindow.ch_TimeWindow) return
          onSuccess()
          syncProgressNote({
            timeWindow: ch_updateTimeWindow.ch_TimeWindow,
            patient,
            onCompleted: (response) => {
              const progressNote = response.ch_updateProgressNotePointOfCareFromTimeWindow.ch_ProgressNote
              if (progressNote) {
                const progressNoteForm = progressNoteFormStore.getState().progressNoteForms.get(progressNote.id)
                if (progressNote.treatmentPlan?.from) {
                  progressNoteForm?.treatmentPlanForm?.setValue(
                    "period.from",
                    new Date(progressNote.treatmentPlan.from),
                  )
                }

                if (progressNote.treatmentPlan?.to) {
                  progressNoteForm?.treatmentPlanForm?.setValue("period.to", new Date(progressNote.treatmentPlan.to))
                }

                progressNoteForm?.pointOfCareForm?.setValue(
                  "careProviderId",
                  progressNote.pointOfCare?.careProvider?.id,
                )
              }
            },
          })
        },
        onError: (error) => {
          toast({
            description: error.message || t("common.errorOccurred"),
          })
        },
      })
    } else {
      mutateTimeWindow({
        kind: "create",
        input: {
          patientId,
          involvesPreventionProgram: data.involvesPreventionProgram,
          referralToCareProviderId,
          referredByExternalCareProviderId,
          referralToDoctorName: data.nameOfDoctor,
          dateFrom: format(data.dateFrom, "yyyy-MM-dd"),
          dateTo: format(data.dateTo, "yyyy-MM-dd"),
          referralReason: data.referralReason,
          referredBy: data.referredBy,
          internalComment: data.internalComment,
          errorCorrection: isCorrection,
          referenceDate,
          inboundCallId: useCallContextStore.getState().inboundCallId,
          alternativeTriageData:
            data.referredBy === "MEDGATE" && data.alternativeTriage && data.alternativeTriagePhoneNumber
              ? {
                  phoneNumber: data.alternativeTriagePhoneNumber,
                  reason: data.alternativeTriageReason ?? "",
                  language: patient.primaryLanguage,
                }
              : null,
        },
        onCreateSuccess: ({ ch_createTimeWindow }) => {
          if (ch_createTimeWindow.ch_TimeWindow) {
            onSuccess()
          }
        },
        onError: (error) => {
          toast({
            description: error.message || t("common.errorOccurred"),
          })
        },
      })
    }
  }
}
